import { CustomHeader } from "@/components/CustomHeader";
import { GroupMembers } from "@/components/GroupMembers"; // Will resolve to .native
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import {
  LocationPicker,
  type LocationValue,
} from "@/components/ui/LocationPicker";
import { useAuth } from "@/context/AuthContext";
import { Group } from "@/types/group";
import firestore, { GeoPoint } from "@react-native-firebase/firestore";
import { getGroupPictureThumbnail } from "@/utils/getGroupPictureThumbnail";
import {
  setGroupImageOverride,
  useGroupImageOverride,
} from "@/utils/groupImageOverrides";
import { isPublicGroup } from "@/utils/groupVisibility";
import storage from "@react-native-firebase/storage";
import { Image } from "expo-image";
import * as ImagePicker from "expo-image-picker";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  StyleSheet,
  Switch,
  TouchableOpacity,
  View,
} from "react-native";

const DefaultGroupImage = require("../../../assets/images/default-group.png");

export default function GroupInfoScreen() {
  const { group: groupString, groupId: groupIdParam } = useLocalSearchParams<{
    group?: string;
    groupId?: string;
  }>();
  const { user } = useAuth();
  const router = useRouter();
  const { t } = useTranslation();
  const initialGroup: Group | null = groupString
    ? (JSON.parse(groupString) as Group)
    : null;
  const groupId = initialGroup?.id ?? groupIdParam ?? "";
  const [group, setGroup] = useState<Group | null>(initialGroup);
  const [uploadingImg, setUploadingImg] = useState(false);
  const overrideUri = useGroupImageOverride(group?.id);

  useEffect(() => {
    if (!groupId) return;
    const unsubscribe = firestore()
      .collection("groups")
      .doc(groupId)
      .onSnapshot((doc) => {
        if (doc.exists()) {
          setGroup({ id: doc.id, ...doc.data() } as Group);
        }
      });
    return () => unsubscribe();
  }, [groupId]);

  const isAdmin = user?.uid === group?.adminId;
  const isMember =
    !!user &&
    Array.isArray(group?.memberIds) &&
    group.memberIds.includes(user.uid);

  useEffect(() => {
    if (group && user && !isMember) {
      router.replace({
        pathname: "/(tabs)/groups/[id]",
        params: { id: group.id },
      });
    }
  }, [group, user, isMember, router]);

  const locationValue = useMemo<LocationValue>(() => {
    if (!group?.location?.geoPoint) return null;
    return {
      latitude: group.location.geoPoint.latitude,
      longitude: group.location.geoPoint.longitude,
      city: group.location.city,
      country: group.location.country,
    };
  }, [group?.location]);

  const handleLocationChange = useCallback(
    async (next: LocationValue) => {
      if (!group || !isAdmin) return;
      const groupRef = firestore().collection("groups").doc(group.id);
      try {
        if (next === null) {
          await groupRef.update({
            location: firestore.FieldValue.delete(),
            allowMemberLocationOverride: firestore.FieldValue.delete(),
          });
        } else {
          await groupRef.update({
            location: {
              geoPoint: new GeoPoint(next.latitude, next.longitude),
              city: next.city || "",
              country: next.country || "",
            },
            allowMemberLocationOverride:
              group.allowMemberLocationOverride ?? false,
          });
        }
      } catch (e) {
        console.error("Failed to update group location", e);
        Alert.alert(t("ERROR"), t("FAILED_TO_UPDATE_GROUP"));
      }
    },
    [group, isAdmin, t],
  );

  const handleToggleOverride = useCallback(
    async (next: boolean) => {
      if (!group || !isAdmin) return;
      try {
        await firestore()
          .collection("groups")
          .doc(group.id)
          .update({ allowMemberLocationOverride: next });
      } catch (e) {
        console.error("Failed to update override flag", e);
        Alert.alert(t("ERROR"), t("FAILED_TO_UPDATE_GROUP"));
      }
    },
    [group, isAdmin, t],
  );

  const handleRequestFlipToPrivate = useCallback(() => {
    if (!group || !isAdmin || !isPublicGroup(group)) return;
    Alert.alert(
      t("MAKE_GROUP_PRIVATE_TITLE"),
      t("MAKE_GROUP_PRIVATE_BODY"),
      [
        { text: t("CANCEL"), style: "cancel" },
        {
          text: t("MAKE_PRIVATE"),
          style: "destructive",
          onPress: async () => {
            try {
              await firestore()
                .collection("groups")
                .doc(group.id)
                .update({ visibility: "private" });
            } catch (e) {
              console.error("Failed to update visibility", e);
              Alert.alert(t("ERROR"), t("FAILED_TO_UPDATE_GROUP"));
            }
          },
        },
      ],
    );
  }, [group, isAdmin, t]);

  const handleChangeGroupImage = async () => {
    if (!group || !isAdmin) return;
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: "images",
      allowsEditing: true,
      aspect: [1, 1],
      quality: 1,
    });
    if (result.canceled) return;
    setUploadingImg(true);
    try {
      const fileUri = result.assets[0].uri;
      const response = await fetch(fileUri);
      const blob = await response.blob();
      const storageRef = storage().ref(
        `group-pictures/${group.id}/original.jpeg`,
      );
      await storageRef.put(blob);
      setGroupImageOverride(group.id, fileUri);
    } catch (error) {
      console.error("Error updating group image: ", error);
      Alert.alert(t("ERROR"), t("FAILED_TO_UPLOAD_IMAGE"));
    } finally {
      setUploadingImg(false);
    }
  };

  if (!group || (user && !isMember)) {
    return (
      <ThemedView style={styles.centered}>
        <ActivityIndicator />
      </ThemedView>
    );
  }

  return (
    <>
      <CustomHeader title={group.name} type="subtitle" showBackButton />
      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        <ThemedView style={styles.container}>
          <ThemedView style={styles.groupImageContainer}>
          <TouchableOpacity
            onPress={handleChangeGroupImage}
            disabled={!isAdmin || uploadingImg}
            accessibilityLabel={isAdmin ? t("CHANGE_GROUP_IMAGE") : undefined}
          >
            <Image
              source={
                overrideUri
                  ? { uri: overrideUri }
                  : {
                      uri: getGroupPictureThumbnail(
                        group.id,
                        group.photoUpdatedAt?.toMillis?.(),
                      ),
                    }
              }
              placeholder={DefaultGroupImage}
              style={styles.groupImage}
              cachePolicy="disk"
              transition={300}
            />
            {uploadingImg && (
              <ThemedView style={styles.uploadingOverlay}>
                <ActivityIndicator size="large" color="#fff" />
              </ThemedView>
            )}
          </TouchableOpacity>
        </ThemedView>
        <ThemedText>{group?.description}</ThemedText>
        {isAdmin && (
          <ThemedView style={styles.visibilitySection}>
            <View style={styles.toggleRow}>
              <View style={styles.toggleTextGroup}>
                <ThemedText style={styles.toggleTitle}>
                  {t("PUBLIC_GROUP")}
                </ThemedText>
                {!isPublicGroup(group) && (
                  <ThemedText style={styles.toggleHint}>
                    {t("PERMANENTLY_PRIVATE_HINT")}
                  </ThemedText>
                )}
              </View>
              <Switch
                value={isPublicGroup(group)}
                onValueChange={() => handleRequestFlipToPrivate()}
                disabled={!isPublicGroup(group)}
              />
            </View>
          </ThemedView>
        )}
        {isAdmin ? (
          <ThemedView style={styles.locationSection}>
            <LocationPicker
              value={locationValue}
              onChange={handleLocationChange}
              label={t("GROUP_LOCATION")}
            />
            {locationValue && (
              <View style={styles.toggleRow}>
                <ThemedText style={styles.toggleLabel}>
                  {t("ALLOW_MEMBER_LOCATION_OVERRIDE")}
                </ThemedText>
                <Switch
                  value={!!group.allowMemberLocationOverride}
                  onValueChange={handleToggleOverride}
                />
              </View>
            )}
          </ThemedView>
        ) : (
          locationValue && (
            <ThemedView style={styles.locationSection}>
              <LocationPicker
                value={locationValue}
                onChange={() => {}}
                readOnly
                label={t("GROUP_LOCATION")}
              />
            </ThemedView>
          )
        )}
        <ThemedView style={{ flexDirection: "column", marginTop: 10, gap: 8 }}>
          {isAdmin && (
            <Button
              text={t("INVITE_USERS")}
              onPress={() =>
                router.push({
                  pathname: "/(tabs)/groups/invite",
                  params: { groupId: group.id, groupName: group.name, group: JSON.stringify(group) },
                })
              }
              style={{ width: "100%", maxWidth: "100%" }}
            />
          )}
        </ThemedView>
        <GroupMembers
          memberIds={group.memberIds}
          adminId={group.adminId}
          groupId={group.id}
        />
      </ThemedView>
      </ScrollView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  groupImageContainer: {
    alignItems: "center",
    marginBottom: 16,
    position: "relative",
  },
  groupImage: {
    width: 120,
    height: 120,
    borderRadius: 60,
  },
  uploadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: 60,
  },
  locationSection: { marginTop: 16 },
  visibilitySection: { marginTop: 16 },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  toggleLabel: { flex: 1, fontSize: 14, marginRight: 12 },
  toggleTextGroup: { flex: 1, marginRight: 12 },
  toggleTitle: { fontSize: 14 },
  toggleHint: { fontSize: 12, opacity: 0.7, marginTop: 2 },
});
